import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  TooltipProps,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";
import {
  buildActualValuesData,
  buildIndexComparisonData,
  getTestColor,
  TestWithResults,
  ComparisonDataPoint,
  IndexComparisonDataPoint,
} from "./utils";

interface ActualValuesChartsProps {
  tests: TestWithResults[];
}

interface ChartDataItem {
  name: string;
  value: number;
  testId: string;
  color: string;
  rawValue?: number;
}

const CustomTooltip: React.FC<
  TooltipProps<number, string> & { unit: string; showIndex: boolean }
> = ({ active, payload, unit, showIndex }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0];
  const item = data.payload as ChartDataItem;

  return (
    <div className="rounded-lg border bg-background p-3 shadow-lg">
      <p className="font-medium text-foreground">{item.name}</p>
      {showIndex ? (
        <>
          <p className="text-sm text-foreground">
            Index {typeof data.value === "number" ? data.value : "N/A"}
          </p>
          {item.rawValue !== undefined && (
            <p className="text-xs text-muted-foreground">
              ({item.rawValue.toFixed(2)} {unit})
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {typeof data.value === "number" ? data.value.toFixed(2) : data.value}{" "}
          {unit}
        </p>
      )}
    </div>
  );
};

interface SingleIndicatorChartProps {
  indicator: ComparisonDataPoint | IndexComparisonDataPoint;
  tests: TestWithResults[];
  showIndex: boolean;
}

const SingleIndicatorChart: React.FC<SingleIndicatorChartProps> = ({
  indicator,
  tests,
  showIndex,
}) => {
  const chartData = useMemo((): ChartDataItem[] => {
    const items: ChartDataItem[] = [];
    tests.forEach((test, index) => {
      const value = indicator[test.id];
      if (typeof value !== "number") {
        return;
      }

      const rawValue = showIndex
        ? (indicator[`${test.id}_raw`] as number | undefined)
        : undefined;

      items.push({
        name: test.name,
        value,
        testId: test.id,
        color: getTestColor(index),
        rawValue,
      });
    });
    return items;
  }, [indicator, tests, showIndex]);

  const unit = indicator.unit || "";

  // Determine domain based on mode
  let domainMin: number;
  let domainMax: number;
  let ticks: number[] | undefined;

  if (showIndex) {
    domainMin = 0;
    domainMax = 5;
    ticks = [0, 1, 2, 3, 4, 5];
  } else {
    // Use the max value from actual data, plus 5% padding
    const actualMaxValue = Math.max(...chartData.map((d) => d.value), 0);
    const padding = actualMaxValue * 0.05;
    domainMin = 0;
    domainMax = actualMaxValue + padding;
  }

  const chartHeight = Math.max(60, chartData.length * 32 + 20);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {indicator.displayName}
          {!showIndex && unit && (
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              ({unit})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-3">
        <div style={{ height: chartHeight }} className="w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
              barSize={18}
            >
              <XAxis
                type="number"
                domain={[domainMin, domainMax]}
                ticks={ticks}
                tick={{ fontSize: 10 }}
                tickFormatter={(value) =>
                  showIndex
                    ? String(value)
                    : typeof value === "number"
                      ? value.toFixed(1)
                      : value
                }
                className="fill-muted-foreground"
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 10 }}
                width={70}
                className="fill-muted-foreground"
              />
              <Tooltip
                content={<CustomTooltip unit={unit} showIndex={showIndex} />}
                cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {chartData.map((entry) => (
                  <Cell key={entry.testId} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export const ActualValuesCharts: React.FC<ActualValuesChartsProps> = ({
  tests,
}) => {
  const [showIndex, setShowIndex] = useState(false);

  const chartData = useMemo(
    () => (showIndex ? buildIndexComparisonData(tests) : buildActualValuesData(tests)),
    [tests, showIndex]
  );

  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent className="flex h-64 items-center justify-center">
          <p className="text-muted-foreground">
            No comparable indicators found in the selected tests.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Indicator Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-3">
              {tests.map((test, index) => (
                <div key={test.id} className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-sm"
                    style={{ backgroundColor: getTestColor(index) }}
                  />
                  <span className="text-sm text-foreground">{test.name}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="show-index"
                checked={showIndex}
                onCheckedChange={(checked) => setShowIndex(Boolean(checked))}
              />
              <Label
                htmlFor="show-index"
                className="cursor-pointer text-sm text-muted-foreground"
              >
                Use index ratings
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {chartData.map((indicator) => (
          <SingleIndicatorChart
            key={indicator.indicator}
            indicator={indicator}
            tests={tests}
            showIndex={showIndex}
          />
        ))}
      </div>
    </div>
  );
};

export default ActualValuesCharts;
